import * as React from "react";
import { useTl, useApiService } from "../../../../hooks";
import { useHistory } from "react-router";
import { useGlobalData, useDialog, useEventsContext } from "../../../../contexts";
import { IDataTableColumn, ButtonContainer, showSuccess, showError } from "nsitools-react";
import { ViewButton, EditButton, SearchTablePage, AddButton, CloseButton } from "../../../../components";
import { ERoutes } from "../../../../ERoutes";
import { ETLCodes } from "../../../../locales";
import {
  WorkAttestRequestGridDto,
  WorkAttestationRequestApi,
  WorkAttestRequestSearch,
  ERequestState,
} from "../../../../api";
import { Link } from "react-router-dom";
import { Checkbox, Intent } from "@blueprintjs/core";

export interface IWorkAttestationRequestListPageProps {}

export const WorkAttestationRequestListPage: React.FunctionComponent<IWorkAttestationRequestListPageProps> = (
  props
) => {
  const { showDialog } = useDialog();
  const { t } = useTl();
  const { dispatchEvent } = useEventsContext();
  const api = useApiService(WorkAttestationRequestApi);
  const history = useHistory();
  const { currentLanguage } = useGlobalData();
  const selectionList = React.useMemo(() => [], []);
  const closableIdsList = React.useMemo(() => [], []);
  const [selectAll, setselectAll] = React.useState(false);

  const toggleSelectAll = React.useCallback(() => {
    console.log("closableIds: ", closableIdsList);
    const value = !selectAll;
    if (value) {
      selectionList.splice(0, selectionList.length, ...closableIdsList);
    } else {
      selectionList.length = 0;
    }
    setselectAll(value);
  }, [closableIdsList, selectAll, selectionList]);

  const closeRequests = React.useCallback(
    (requestList) => {
      if (selectionList.length === 0) {
        showDialog({
          message: t(ETLCodes.CloseRequestSelectionEmpty),
          title: t(ETLCodes.CloseRequests),
          buttons: [{ resultType: "yes", text: t(ETLCodes.General_Ok), intent: Intent.PRIMARY }],
          onConfirmed: async () => {},
        });
      } else {
        showDialog({
          message: t(ETLCodes.CloseRequestConfirmationMessage),
          title: t(ETLCodes.CloseRequests),
          onConfirmed: async () => {
            try {
              await requestList.forEach((element) => {
                api.closeRequest(element);
              });
              selectionList.splice(0, selectionList.length);
              dispatchEvent("RefreshSearchTables", 0);
              showSuccess(t(ETLCodes.RequestsClosedSuccess));
            } catch (error) {
              showError(t(ETLCodes.Error));
            }
          },
        });
      }
    },
    [api, dispatchEvent, selectionList, showDialog, t]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => <Checkbox onChange={toggleSelectAll} />,
        computed: true,
        disableSorting: true,
        fieldName: "selector",
        autoFitContent: true,
        render: (row: WorkAttestRequestGridDto) => {
          const disabled = row.requestStateCode !== ERequestState.PAID;
          if (!disabled) {
            closableIdsList.push(row.workAttestRequestId);
          }
          return (
            <Checkbox
              checked={selectionList.find((c) => c === row.workAttestRequestId)}
              onChange={() => {
                if (selectionList.includes(row.workAttestRequestId)) {
                  const index = selectionList.findIndex((e) => e === row.workAttestRequestId);
                  selectionList.splice(index, 1);
                } else {
                  selectionList.push(row.workAttestRequestId);
                }
              }}
              disabled={disabled}
            />
          );
        },
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: WorkAttestRequestGridDto) => (
          <ButtonContainer>
            <ViewButton
              minimal={true}
              onClick={() => history.push(`${ERoutes.workAttestationRequest}/${row.workAttestRequestId}/detail/view`)}
            />
            <EditButton
              minimal={true}
              onClick={() => history.push(`${ERoutes.workAttestationRequest}/${row.workAttestRequestId}/detail/edit`)}
              disabled={row.requestStateCode === ERequestState.EXECUTED}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.RequestBlocked),
        fieldName: "isBlocked",
        alignment: "center",
        render: (row: WorkAttestRequestGridDto) => <Checkbox checked={row.isBlocked} disabled />,
      },
      {
        header: () => t(ETLCodes.RequestNumber),
        fieldName: "requestNumber",
      },
      {
        header: () => t(ETLCodes.RequestDate),
        fieldName: "requestDate",
      },
      {
        header: () => t(ETLCodes.RequestState),
        fieldName: "requestState",
      },
      {
        header: () => t(ETLCodes.Dog),
        fieldName: "dog",
        render: (row: WorkAttestRequestGridDto) => (
          <Link target="_blank" to={`${ERoutes.dog}/${row.dogId}/detail/view`}>
            {row.dog}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.CompetitionCountry),
        fieldName: "competitionCountry",
      },
      {
        header: () => t(ETLCodes.CompetitionDate),
        fieldName: "competitionDate",
      },
      {
        header: () => t(ETLCodes.Contact),
        fieldName: "contact",
        render: (row: WorkAttestRequestGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.personId}/detail/view`}>
            {row.contact}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Invoice),
        fieldName: "invoiceNumber",
      },
    ],
    [closableIdsList, history, selectionList, t, toggleSelectAll]
  );

  const addItemFunction = React.useCallback(() => {
    history.push(`${ERoutes.workAttestationRequest}/0/detail/edit`);
  }, [history]);
  const search = React.useCallback(
    (sObj) => api.baseSearch(WorkAttestRequestSearch.fromJS({ ...sObj, languageId: currentLanguage.languageId })),
    [api, currentLanguage.languageId]
  );

  return (
    <SearchTablePage
      columns={columns}
      getCriteriasFunction={() => api.getSearchCriterias(true)}
      searchFunction={search}
      exportFunction={(sObj) =>
        api.exportXlsFile(WorkAttestRequestSearch.fromJS({ ...sObj, languageId: currentLanguage.languageId }))
      }
      sortKeys={{ requestNumber: "ASC" }}
      breadCrumbs={[{ text: t(ETLCodes.WorkAttestationRequests) }]}
      rightElement={
        <div>
          <AddButton text={t(ETLCodes.General_Add)} onClick={() => addItemFunction()} />
          <CloseButton text={t(ETLCodes.Close)} onClick={() => closeRequests(selectionList)} />
        </div>
      }
    />
  );
};
